import { getIronSession } from 'iron-session'
import type {
  cookies as cookiesFactory,
  headers as headersFactory,
} from 'next/headers'
import type { NextRequest, NextResponse } from 'next/server'

import { logger } from '@mntn-dev/logger'

import { options } from './options.ts'
import {
  COOKIE_PARAMS_HEADER,
  type CookieParams,
  CookieParamsSchema,
} from './types.ts'
import { findCookieParamsInSearchParams } from './utils.ts'

export const CookieParamsCookie = {
  get: async (
    cookies: Awaited<ReturnType<typeof cookiesFactory>>,
    headers: Awaited<ReturnType<typeof headersFactory>>
  ) => {
    const cookieParams = await getIronSession<CookieParams>(cookies, options)

    const headerParamsRaw = headers.get(COOKIE_PARAMS_HEADER)
    const headerParams = headerParamsRaw
      ? JSON.parse(decodeURIComponent(headerParamsRaw))
      : {}

    // Merge cookie-based params with header-forwarded params.
    // On the first request, cookies written to the response aren’t yet visible on the request,
    // so COOKIE_PARAMS_HEADER acts as a one-request bridge. On subsequent requests, cookies are the source of truth.
    return CookieParamsSchema.parse({ ...cookieParams, ...headerParams })
  },
  processRequest: async (
    req: NextRequest,
    res: NextResponse
  ): Promise<void> => {
    try {
      const newParams = findCookieParamsInSearchParams(req.nextUrl.searchParams)

      if (Object.keys(newParams).length === 0) {
        return
      }

      const session = await getIronSession<CookieParams>(req, res, options)

      Object.assign(session, CookieParamsSchema.parse(newParams))

      await session.save()

      // Forward cookie params (parsed from the query string) via a *request* header so the
      // server layout can read them via headers() on the *same request* ("first-render parity").
      // Cookies written on the response are not visible to that in-flight render,
      // so this header is a one-request bridge. On subsequent requests, the cookie is canonical.
      req.headers.set(
        COOKIE_PARAMS_HEADER,
        encodeURIComponent(JSON.stringify(newParams))
      )
    } catch (error) {
      logger.error('Encountered unexpected error writing to CookieParams', {
        error,
      })
    }
  },
}
